import React from 'react';
import Link from 'next/link';
import clsx from 'clsx';
import { type BuyerLayoutProps } from 'app/shop-ui';
import { ELEMENT_TYPE } from 'shop-ui/constants';
import { getDisplayPrice } from 'app/libs/getDisplayPrice';
import i18n from '@shared/i18n';
import { Picture, type PictureSourceType } from 'app/components/Picture';
import './ProductCard.scss';

const DEFAULT_BADGE = i18n.gettext('0 元購');

export interface ProductCardPropOptionsType {
    usePriceBadge?: boolean;
    useShadow?: boolean;
    useSquareImage?: boolean;
    useProductName?: boolean;
    useProductPrice?: boolean;
    useProductBadge?: boolean;
    buttonOnClick?: () => void;
    useButton?: boolean;
    buttonContent?: string;
    fullWidth?: boolean;
    componentType: ELEMENT_TYPE;
}

export interface ProductCardPropType
    extends ProductCardPropOptionsType,
        Partial<BuyerPromoProduct>,
        BuyerLayoutProps {
    isEditingMode?: boolean;
    className?: string;
    source: PictureSourceType;
    alt: string;
}

function getProductBadge(
    type: ELEMENT_TYPE,
    isOutOfStock: boolean,
    badge: string | undefined,
    isBuyer?: boolean
) {
    if (isOutOfStock) {
        return i18n.gettext('售完');
    }

    if (!isBuyer && type === ELEMENT_TYPE.PRODUCT_GIVEAWAY) {
        return DEFAULT_BADGE;
    }

    return type === ELEMENT_TYPE.PRODUCT_GIVEAWAY ? badge : null;
}

export const ProductCard: React.FC<ProductCardPropType> = (props) => {
    const {
        componentType,
        name,
        id: productId,
        price: originalPrice,
        discounted_price: discountedPrice,
        min_add_on_price: minAddOnPrice,
        usePriceBadge,
        useShadow,
        useProductName,
        useProductPrice,
        buttonOnClick,
        buttonContent,
        useButton,
        source,
        alt,
        className,
        badge,
        useProductBadge = true,
        fullWidth,
        total_stock,
        isBuyer,
    } = props;

    const isOutOfStock = isBuyer
        ? typeof total_stock === 'number' && total_stock === 0
        : false;

    const productBadge = getProductBadge(componentType, isOutOfStock, badge, isBuyer);

    const displayPrice = getDisplayPrice({
        originalPrice,
        discountedPrice,
        minAddOnPrice,
        promoSavingPrice: null,
    });

    return (
        <div
            className={clsx(
                'product-card',
                className,
                fullWidth && 'product-card--fullWidth'
            )}
        >
            <Link
                href={`/product/${productId}`}
                className={clsx(
                    'product-card__upper-area',
                    useShadow && 'product-card__upper-area--shadow'
                )}
            >
                {productBadge && (useProductBadge || isOutOfStock) && (
                    <div
                        className={clsx('product-card__badge', {
                            'product-card__badge--out-of-stock': isOutOfStock,
                        })}
                    >
                        {productBadge}
                    </div>
                )}
                <div className="product-card__image-box">
                    <div className="product-card__image">
                        <Picture
                            className="product-card__image__picture"
                            sources={source}
                            alt={alt}
                        />
                    </div>
                    {useProductPrice && usePriceBadge && (
                        <>
                            {displayPrice.useAddOnPrice && (
                                <div className="product-card__add-on-price">
                                    {displayPrice.minAddOnPrice?.value}{' '}
                                    {i18n.gettext('起')}
                                </div>
                            )}
                            {!displayPrice.useAddOnPrice && (
                                <span className="product-card__price-badge">
                                    {displayPrice.discountedPrice?.value ||
                                        displayPrice.originalPrice?.value}
                                </span>
                            )}
                        </>
                    )}
                </div>

                <div className="product-card__content">
                    {useProductName && (
                        <strong className="product-card__content__title">{name}</strong>
                    )}

                    {useProductPrice && !usePriceBadge && (
                        <div className="product-card__price-box">
                            {displayPrice.useAddOnPrice && (
                                <span className="product-card__price">
                                    {displayPrice.minAddOnPrice?.value}{' '}
                                    {i18n.gettext('起')}
                                </span>
                            )}
                            {!displayPrice.useAddOnPrice && (
                                <>
                                    {displayPrice.useDiscountedPrice &&
                                        displayPrice.discountedPrice && (
                                            <span className="product-card__price">
                                                {displayPrice.discountedPrice?.value}
                                            </span>
                                        )}
                                    <b className="product-card__price">
                                        {displayPrice.originalPrice?.value}
                                    </b>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </Link>

            {useButton && (
                <div className="product-card__lower-area">
                    <button className="product-card__button" onClick={buttonOnClick}>
                        {buttonContent}
                    </button>
                </div>
            )}
        </div>
    );
};
